// Same maths as api/quantities.py, so the calculator agrees with the project
// page and the quotation. Sizes are whole millimetres.

export const SOLD_AS = [
  { key: "area", label: "Printed to size", unit: "sq ft", per: "per sq ft" },
  { key: "roll", label: "Rolls", unit: "roll", per: "per roll" },
  { key: "panel", label: "Panels", unit: "panel", per: "per panel" },
  { key: "piece", label: "Pieces", unit: "piece", per: "per piece" },
];
export const UNITS = Object.fromEntries(SOLD_AS.map((entry) => [entry.key, entry.unit]));
export const DEFAULT_WASTAGE = { area: 10, roll: 0, panel: 0, piece: 0 };
const SQ_FT_MM2 = 92903.04;
export const ROLL_TRIM_MM = 100;
const ART_SHARE = [0.57, 0.75];
export const ART_CENTRE_MM = 1500;
const EPSILON = 1e-9;

const ceilDiv = (a, b) => Math.ceil(a / b - EPSILON);
const withWastage = (count, wastage) => Math.ceil((count * (100 + wastage)) / 100 - EPSILON);

export function calculate(soldAs, { wallW, wallH, width, height, repeat, wastage } = {}) {
  const kind = UNITS[soldAs] ? soldAs : "piece";
  const extra = Number(wastage ?? DEFAULT_WASTAGE[kind]);
  const result = { soldAs: kind, unit: UNITS[kind], quantity: null, missing: [], notes: [], detail: {} };
  const hasWall = Boolean(wallW && wallH);

  if (kind === "piece") {
    result.quantity = 1;
    if (hasWall) {
      result.detail = {
        suggestedWidth: ART_SHARE.map((share) => Math.round(wallW * share)),
        centreHeight: ART_CENTRE_MM,
        fits: width && height ? width <= wallW && height <= wallH : null,
      };
    }
    return result;
  }

  if (!hasWall) result.missing.push("wall");
  if ((kind === "roll" || kind === "panel") && !(width && height)) result.missing.push(kind);
  if (result.missing.length) return result;

  if (kind === "area") {
    result.quantity = Math.ceil((wallW * wallH * (100 + extra)) / (SQ_FT_MM2 * 100) - EPSILON);
    result.detail = { areaSqFt: Math.round(((wallW * wallH) / SQ_FT_MM2) * 100) / 100 };
    return result;
  }

  if (kind === "roll") {
    let drop = wallH + ROLL_TRIM_MM;
    if (repeat) drop = ceilDiv(drop, repeat) * repeat;
    const strips = ceilDiv(wallW, width);
    const perRoll = Math.floor(height / drop);
    let rolls;
    if (perRoll >= 1) {
      rolls = ceilDiv(strips, perRoll);
    } else {
      rolls = strips * ceilDiv(drop, height);
      result.notes.push("The wall is taller than one roll, so each strip needs a join.");
    }
    result.quantity = withWastage(rolls, extra);
    result.detail = { strips, drop, stripsPerRoll: perRoll };
    return result;
  }

  const across = ceilDiv(wallW, width);
  const down = ceilDiv(wallH, height);
  result.quantity = withWastage(across * down, extra);
  result.detail = { across, down, covers: [across * width, down * height] };
  return result;
}
